import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { inject, observer } from 'mobx-react';
import compose from 'helpers/compose';
import { rentIsZero } from 'helpers/targets';

import Grid from '@material-ui/core/Grid';
import EditIcon from '@material-ui/icons/EditOutlined';
import IconButton from '@material-ui/core/IconButton';
import DeleteIcon from '@material-ui/icons/DeleteOutlined';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Collapse from '@material-ui/core/Collapse';
import Link from '@material-ui/core/Link';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import AddIcon from '@material-ui/icons/Add';

import { formatAmountValue, pluralize, sqlDateToLabel, fractionToPercent } from 'helpers/helpers';
import RetirementIcon from 'components/Icons/RetirementIcon';
import Info from 'components/Tooltip/Info';
import BarGraph, { BarData } from 'components/Graph/BarGraph';
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog';
import Tooltip from 'components/Tooltip/Tooltip';

import { CalculationMode } from 'common/interfaces';
import AdditionalTargetModal from './AdditionalTargetModal';
import RentModals from 'pages/Accounts/Edit/components/Rent/RentModals';
import { ViewMode } from './viewModes';
import { TargetsStore } from 'stores/workflowStores/finances/targets/targetsStore';
import { Target, TargetFrequency, TargetsData } from 'stores/workflowStores/finances/targets/model';
import { FulfillmentGrade } from 'stores/workflowStores/finances/targets/model';
import { RootStore } from 'stores/rootStore';
import { WorkflowPlanStore } from 'stores/workflowStores/newWorkflowPlanStore/workflowPlanStore';
import { RentData } from 'stores/workflowStores/finances/rent/model';
import useStyles from './styles/moduleStyles';
import { fulfillmentIcon, typeIcon } from './iconHelper';
import { EdwardColors, PersonType, Currency, AccountMode } from 'enums';
import { AccountBasic } from 'stores/workflowStores/workflowBasicStore/model';

interface Props {
  accountId: string;
  currency: Currency;
  rootStore: RootStore;
  rentData?: RentData;
  barData?: BarData[];
  targetsData?: TargetsData;
  plan?: boolean;
  refresh?: () => void;
  readOnly?: boolean;
  userMode?: boolean;
  mode?: AccountMode;
  viewMode?: ViewMode;
  moduleInfo?: string;
  targetsOnly?: boolean;
  collapsed?: boolean;
  personType: PersonType;
  planStore?: WorkflowPlanStore;
  defaultInflation: number;
  defaultInflationType: string;
  overview?: boolean;
  original?: boolean;
  rentApplied?: boolean;
  planBasicData?: AccountBasic;
}

const AdditionalTargets: React.FC<Props> = ({
  accountId,
  rentData,
  barData,
  targetsData,
  currency,
  rootStore,
  plan,
  refresh,
  readOnly,
  userMode,
  viewMode,
  mode,
  moduleInfo,
  targetsOnly,
  collapsed,
  personType,
  planStore,
  defaultInflation,
  defaultInflationType,
  overview,
  original,
  rentApplied,
  planBasicData,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const translatedCurrency = useMemo(() => t(`currency_${currency}`), [t, currency]);

  const [isAdditionalTargetOpen, setIsAdditionalTargetOpen] = useState(false);
  const [isRentOpen, setIsRentOpen] = useState(false);
  const [targetId, setTargetId] = useState<string | undefined>(undefined);
  const [targetsStore] = useState(new TargetsStore(rootStore, accountId, viewMode));
  const [showDetail, setShowDetail] = useState(
    (targetsData && targetsData.targets.length > 0) || rentData !== undefined || !collapsed,
  );
  const [showIQinfoDialog, setShowIQinfoDialog] = useState(false);
  const [allTargetsCount, setAllTargetsCount] = useState(targetsData?.targets.length || 0);

  const targetsCount = targetsData?.targets ? targetsData?.targets.length : 0;
  targetsData = targetsData ? targetsData : targetsStore.allTargets || undefined;

  useEffect(() => {
    if (!targetsData) {
      targetsStore.fetchAllTargets();
    }
  }, [accountId, targetsData, targetsStore, collapsed]);

  useEffect(() => {
    setAllTargetsCount(targetsData?.targets.length || 0);
  }, [targetsData]);

  const editTargetId = planStore?.editTarget;

  useEffect(() => {
    editTargetId && editTarget(editTargetId);
  }, [editTargetId]);

  const showIQinfo = useCallback(() => {
    if (!rentData && targetsCount === 0 && plan) {
      setShowIQinfoDialog(true);
    }
  }, [rentData, targetsCount, plan]);

  const editRentOn = planStore?.editRent;

  const editRent = useCallback(() => {
    showIQinfo();
    setIsRentOpen(true);
  }, [showIQinfo]);

  useEffect(() => {
    editRentOn && editRent();
  }, [editRent, editRentOn]);

  const handleAdditionalTargetClose = () => {
    setIsAdditionalTargetOpen(false);
    planStore && planStore.setEditTarget(null);
  };

  const handleRentClose = () => {
    setIsRentOpen(false);
    planStore && planStore.setEditRent(false);
  };

  const editTarget = (targetId: string) => {
    setTargetId(targetId);
    setIsAdditionalTargetOpen(true);
  };

  const handleRefresh = () => {
    if (refresh) {
      refresh();
    } else {
      targetsStore.fetchAllTargets();
    }
  };

  const [confirmDialog, setConfirmDialog] = useState<string | undefined>(undefined);
  const [confirmRentDialog, setConfirmRentDialog] = useState(false);

  const listTargets = (targets: Target[], fullWidth: boolean) => {
    return (
      <>
        {targets.map((target) => {
          return (
            <Grid
              container
              item
              xs={12}
              sm={12}
              md={12}
              lg={!plan || fullWidth ? 12 : 6}
              spacing={1}
              key={target.id}
              alignItems="stretch"
            >
              <Grid
                container
                item
                alignContent="center"
                alignItems="center"
                className={`${classes.item} ${
                  (overview && original) || !target.enabled ? classes.inactive : classes.target
                }`}
              >
                <Grid item xs={2} sm={2} xl={1} className={classes.icon}>
                  {typeIcon(
                    target.type,
                    (overview && original) || !target.enabled ? EdwardColors.GRAY_MEDIUM : undefined,
                  )}
                </Grid>
                <Grid
                  item
                  container
                  xs={userMode || overview ? 10 : 8}
                  sm={userMode || overview ? 10 : 8}
                  xl={userMode || overview ? 11 : 9}
                  spacing={1}
                  alignContent="center"
                  alignItems="center"
                >
                  <Grid item xs={12} sm={plan ? 8 : 12} className={classes.info}>
                    <Typography variant="h3" className={`${classes.title}`} title={target.title}>
                      {target.title}
                    </Typography>
                    {target.frequency === TargetFrequency.SINGLE ? (
                      <span>{sqlDateToLabel(target.since)}</span>
                    ) : (
                      <>
                        <span>
                          {t('general_since')}: {sqlDateToLabel(target.since)}
                        </span>{' '}
                        <span>
                          {t('general_until')}: {sqlDateToLabel(target.until)}
                        </span>
                      </>
                    )}
                    <span>
                      <strong>{formatAmountValue(target.value.value, currency).replace(/ /g, '\u00A0')}</strong>
                      &nbsp;
                      {target.frequency === TargetFrequency.REPEAT_EVERY ? (
                        <>
                          {' '}
                          {t('additionalTargets_every')} {target.repeatYears}
                          {' '}
                          {pluralize(target.repeatYears, t('general_repeatYear'), t('general_repeatYears'))}
                        </>
                      ) : (
                        t(`general_frequency_${target.frequency}`)
                      )}{' '}
                    </span>
                    {target.inflation.enabled && (
                      <span>
                        {t('general_inflationPlanned')}: {fractionToPercent(target.inflation.rate)}
                      </span>
                    )}
                  </Grid>

                  {plan && (
                    <Grid item xs={12} sm={4} className={classes.active}>
                      {target.active && <strong>{t('additionalTargets_active')}</strong>}
                    </Grid>
                  )}
                </Grid>
                {target.fulfillmentGrade && (
                  <>
                    <div className={`${classes.grade} grade_SHADOW`} />
                    <div
                      className={`${classes.grade} ${
                        target.fulfillmentGrade
                          ? 'grade_' + (overview && original ? 'NONE' : target.fulfillmentGrade)
                          : ''
                      }`}
                    >
                      {target.fulfillmentGrade === FulfillmentGrade.IN_PROCESS ? (
                        t('additionalTargets_fulfillmentGrade_IN_PROCESS')
                      ) : (
                        <>
                          {fulfillmentIcon(target.fulfillmentGrade)}{' '}
                          {t(`additionalTargets_fulfillmentGrade_${target.fulfillmentGrade}`)}
                          {target.fulfillment !== undefined && target.fulfillment > 0 && (
                            <span>
                              {fractionToPercent(target.fulfillment, 0)}
                              <Info inline content={t('additionalTargets_fulfillment_tooltip')} />
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  </>
                )}
                {!target.enabled && (
                  <div className={`${classes.grade} grade_NONE`}>
                    {t('additionalTargets_inactive')}
                    <span>
                      <Info inline content={t('additionalTargets_switch_plan_excluded')} />
                    </span>
                  </div>
                )}
                {!userMode && !overview && (
                  <Grid item xs={2} className={classes.actions}>
                    <Tooltip title={readOnly ? t('accountDetail_readOnlyInfo') : ''}>
                      <div>
                        <IconButton
                          size="medium"
                          className="accountEdit-button"
                          onClick={() => editTarget(target.id!)}
                          disabled={readOnly}
                        >
                          <EditIcon fontSize="inherit" />
                        </IconButton>
                        <IconButton
                          size="medium"
                          className="account-delete-button"
                          onClick={() => setConfirmDialog(target.id)}
                          disabled={readOnly}
                        >
                          <DeleteIcon fontSize="inherit" />
                        </IconButton>
                      </div>
                    </Tooltip>
                  </Grid>
                )}
              </Grid>
            </Grid>
          );
        })}
      </>
    );
  };

  const renderRent = (rentData?: RentData, addingEnabled?: boolean, addRentClass = true) => {
    return (
      <Grid
        container
        item
        className={`${classes.item} ${overview && original ? classes.inactive : classes.target} ${
          rentData ? classes.rentWithData : ''
        } ${addRentClass ? classes.rentTarget : ''}`}
        alignContent="center"
        alignItems="center"
      >
        <Grid
          item
          xs={2}
          sm={2}
          xl={1}
          className={`${classes.icon} ${rentData ? classes.rentIcon : ''} ${overview ? 'overview' : ''}`}
        >
          <RetirementIcon fontSize="inherit" htmlColor={overview && original ? EdwardColors.GRAY_MEDIUM : undefined} />
        </Grid>
        <Grid container item xs={userMode ? 10 : 7} sm={userMode ? 10 : 7} xl={userMode ? 11 : 8} spacing={1}>
          <Grid item className={classes.info}>
            <Typography variant={rentData ? 'h2' : 'h3'} className={`${classes.title} rentTitle`}>
              {t('finQuestionnary_pension_lifetimeRentPortfolio')}
            </Typography>
            {rentData &&
              (rentIsZero(rentData) ? (
                <span>
                  {t('additionalTargets_zeroRent')} <Info content={t('additionalTargets_zeroRent_tooltip')} inline />
                </span>
              ) : (
                <>
                  <span>
                    {t('general_since')}: {sqlDateToLabel(rentData.start)}
                  </span>
                  <span>
                    {t([
                      `calculationMode_rent_allowance_${rentData.portfolioRentCalculationMode}`,
                      `calculationMode_rent_${rentData.portfolioRentCalculationMode}`,
                    ])}
                  </span>
                  <span>
                    <strong>
                      {rentData.portfolioRentCalculationMode === CalculationMode.CALCULATED &&
                        formatAmountValue(rentData.calculatedPortfolioRent.value, currency)}
                      {rentData.portfolioRentCalculationMode === CalculationMode.MANUAL &&
                        formatAmountValue(rentData.plannedPortfolioRent.value, currency)}
                      {rentData.portfolioRentCalculationMode === CalculationMode.REQUIRED &&
                        rentData.requiredPortfolioRent &&
                        formatAmountValue(rentData.requiredPortfolioRent.value, currency)}
                    </strong>
                    &nbsp;
                    {t('general_frequency_MONTHLY')}
                  </span>
                  {rentData.inflation.enabled && (
                    <span>
                      {t('general_inflationPlanned')}: {fractionToPercent(rentData.inflation.rate)}
                    </span>
                  )}

                  {rentData.durabilityUntil ? (
                    <span>
                      {t('general_Until')}:&nbsp;
                      {personType === PersonType.PHYSICAL ? (
                        <>
                          <strong>
                            {rentData.durability.years}
                            {rentData.partnerDurability && `/${rentData.partnerDurability.years}`} {t('general_years')}
                          </strong>
                          &nbsp;
                          <span className={classes.helperText}>
                            (
                            {`${t('client.age').toLocaleLowerCase()}${
                              rentData.partnerDurability ? `/${t('general_partners')}` : ''
                            }`}
                            )
                          </span>
                        </>
                      ) : (
                        <strong> {sqlDateToLabel(rentData.durabilityUntil)}</strong>
                      )}
                    </span>
                  ) : (
                    <span>
                      <strong> {t(`general_allTime${personType === PersonType.LEGAL ? '_legal' : ''}`)}</strong>
                    </span>
                  )}
                </>
              ))}
          </Grid>
        </Grid>

        {!userMode && !overview && (
          <>
            {rentData && <Grid item className={`${classes.rentBuffer2}`} />}
            <Grid item xs={3} className={classes.actions}>
              <Tooltip title={readOnly ? t('accountDetail_readOnlyInfo') : ''}>
                <div>
                  <IconButton
                    size="medium"
                    className="accountEdit-button"
                    onClick={() => setIsRentOpen(true)}
                    disabled={readOnly}
                  >
                    <EditIcon fontSize="inherit" />
                  </IconButton>
                  <IconButton
                    size="medium"
                    className="account-delete-button"
                    onClick={() => setConfirmRentDialog(true)}
                    disabled={readOnly}
                  >
                    <DeleteIcon fontSize="inherit" />
                  </IconButton>
                </div>
              </Tooltip>
            </Grid>
          </>
        )}
        {rentData && rentData.portfolioRentCalculationMode !== CalculationMode.MANUAL && (
          <>
            <Grid
              item
              xs={2}
              sm={2}
              xl={1}
              className={`${classes.icon} ${rentData ? classes.rentIcon + ' ' + classes.rentBuffer : ''}`}
            />
            <Grid item xs={12} className={classes.rentGraph}>
              <BarGraph
                data={rentData}
                plan={true}
                translatedCurrency={translatedCurrency}
                currency={currency}
                original={original}
              />
            </Grid>
          </>
        )}
      </Grid>
    );
  };

  const renderConfirmDialog = (type: 'target' | 'rent') => {
    return (
      <ConfirmDialog
        noHeader
        isOpen={(type === 'target' && confirmDialog !== undefined) || (type === 'rent' && confirmRentDialog)}
        confirm={
          type === 'target'
            ? async () => {
                await targetsStore?.deleteTarget(confirmDialog!);
                setConfirmDialog(undefined);
                handleRefresh();
              }
            : async () => {
                await targetsStore?.deleteRent();
                setConfirmRentDialog(false);
                handleRefresh();
              }
        }
        close={() => {
          type === 'target' ? setConfirmDialog(undefined) : setConfirmRentDialog(false);
        }}
      >
        {t(type === 'target' ? 'additionalTargets_confirmDelete' : 'additionalTargets_confirmDeleteRent')}
      </ConfirmDialog>
    );
  };

  const renderRentTargetsLayout = (targetsData: TargetsData) => {
    return (
      <>
        {rentData && personType === PersonType.PHYSICAL && renderRent(rentData, !readOnly && rentData?.addingEnabled)}
        {listTargets(targetsData.targets, false)}
      </>
    );
  };

  return targetsData ? (
    <>
      {!targetsOnly && !userMode && personType === PersonType.PHYSICAL && (
        <>
          <RentModals
            opened={isRentOpen}
            handleClose={handleRentClose}
            defaultInflation={defaultInflation}
            defaultInflationType={defaultInflationType}
            options={targetsData.options}
            accountId={accountId}
            personType={personType}
            plan
            detailMode={viewMode === ViewMode.DETAIL}
            planBasicData={planBasicData}
            rentApplied={rentApplied}
            existingFlow={rentData?.portfolioRentCalculationMode}
            refresh={refresh}
            mode={mode}
          />
        </>
      )}

      <AdditionalTargetModal
        opened={isAdditionalTargetOpen}
        handleClose={handleAdditionalTargetClose}
        targetId={targetId}
        targetsStore={targetsStore}
        planStore={planStore}
        currency={currency}
        refresh={handleRefresh}
        options={targetsData.options}
        defaultInflation={defaultInflation}
        defaultInflationType={defaultInflationType}
        mode={mode}
      />

      <ConfirmDialog
        noHeader
        isOpen={showIQinfoDialog}
        confirm={() => {
          setShowIQinfoDialog(false);
        }}
        btnConfirmText="general_agreed"
      >
        {t('additionalTargets_IQinfo')}
      </ConfirmDialog>

      {renderConfirmDialog('target')}
      {!targetsOnly && renderConfirmDialog('rent')}

      {!overview && (
        <Grid container spacing={0}>
          <Grid item>
            <Typography variant="h3" className={classes.moduleTitle}>
              {collapsed !== undefined ? (
                <Link
                  className={classes.showLink}
                  id="toggle-targets-link"
                  onClick={(e: React.MouseEvent) => {
                    e.preventDefault();
                    setShowDetail(!showDetail);
                  }}
                >
                  {t('finQuestionnary_target_otherFinancialTargets')}
                  <span className={classes.count}> ({allTargetsCount + (rentData && !targetsOnly ? 1 : 0)})</span>
                  <ArrowDropDownIcon
                    htmlColor={EdwardColors.DARK}
                    className={`${classes.showIcon} ${showDetail ? classes.shownIcon : ''}`}
                  />
                </Link>
              ) : (
                t('finQuestionnary_target_otherFinancialTargets')
              )}
            </Typography>
          </Grid>
          <Grid item style={{ marginLeft: plan ? undefined : '16px' }}>
            {!readOnly &&
              !targetsOnly &&
              (!rentData || rentData.addingEnabled) &&
              plan &&
              personType === PersonType.PHYSICAL && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    showIQinfo();
                    setIsRentOpen(true);
                  }}
                  startIcon={<AddIcon fontSize="medium" />}
                  style={{ marginRight: '8px' }}
                >
                  {t('add_rent')}
                </Button>
              )}
            {!readOnly && targetsData.addingEnabled && !userMode && (
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  showIQinfo();
                  setTargetId(undefined);
                  setIsAdditionalTargetOpen(true);
                }}
                startIcon={<AddIcon fontSize="medium" />}
              >
                {t('add_target')}
              </Button>
            )}
          </Grid>
        </Grid>
      )}

      {moduleInfo && <Typography>{moduleInfo}</Typography>}
      <Collapse in={showDetail} className={classes.collapse}>
        <Grid container spacing={0} alignItems="stretch" className={classes.moduleWrapper}>
          {targetsOnly ? listTargets(targetsData.targets, false) : renderRentTargetsLayout(targetsData)}
        </Grid>
      </Collapse>
    </>
  ) : null;
};

export default compose(inject('rootStore'), observer)(AdditionalTargets);
